import { SetStateAction, Dispatch, useState, useEffect } from 'react';
import classNames from 'classnames';

import { backendFunction } from '~/clients/firebase/client';

import { Data } from 'backend/functions/titles/getSelectedListing';
import { Condition, Book, Title } from 'types';

import styles from '~/styles/components/product/title-attributes.module.scss';
import { mapQueryBy } from 'sdk';
import { Typesense } from 'sdk/src/clients/typesense/react-query';
import { MultiSearchRequestSchema } from 'typesense/lib/Typesense/MultiSearch';

// TODO - Business logic, consider moving to helper
function formatCondition(condition: Condition) {
  if (condition === 'New' || condition === 'Like New') {
    return 'New';
  }

  return 'Used';
}

export default function TitleAttributes({
  title,
  setSelectedListing,
}: {
  title: Title;
  setSelectedListing: Dispatch<SetStateAction<Book>>;
}) {
  const [loading, setLoading] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState(title.selected_listing?.format.display);
  const [selectedCondition, setSelectedCondition] = useState<'Used' | 'New'>(
    formatCondition(title.selected_listing?.condition)
  );

  useEffect(() => {
    if (!title.selected_listing?.format.display) return;

    setSelectedFormat(title.selected_listing.format.display);
  }, [title.selected_listing?.format.display]);

  useEffect(() => {
    if (!title.selected_listing?.condition) return;

    setSelectedCondition(formatCondition(title.selected_listing?.condition));
  }, [title.selected_listing?.condition]);

  async function getSelectedListingForAttributes(attributes: Data) {
    const selectedListing = await backendFunction<Book, Data>('titles-getSelectedListing', attributes);

    return selectedListing;
  }

  async function handleFacetClick({ condition, format }: { condition?; format?; }) {
    if (loading) return;

    const conditionToQuery = condition || selectedCondition;
    const formatToQuery = format || selectedFormat;

    setLoading(true);

    if (condition) {
      setSelectedCondition(condition);
    }

    if (format) {
      setSelectedFormat(format);
    }

    const listing = await getSelectedListingForAttributes({
      title_id: title.id,
      condition: conditionToQuery,
      format: formatToQuery,
    });

    setSelectedListing(listing);
    setLoading(false);
  }

  function conditionToTypesenseFilter(condition: 'Used' | 'New') {
    if (condition === 'New') {
      return 'condition:New';
    }

    if (condition === 'Used') {
      return 'condition:[Excellent, Like New, Good, Fair]';
    }
  }

  function formatToTypesenseFilter(format: 'Hardcover' | 'Paperback') {
    return format === 'Hardcover' ? 'format.display:Hardcover' : 'format.display:Paperback';
  }

  function titleIdToTypesenseFilter(titleId: string) {
    return `title_id:${titleId}`;
  }

  const searchRequest = Typesense<any>().multiSearch({
    props: {
      commonParams: {
        q: '*',
        query_by: mapQueryBy('books'),
        collection: 'books'
      },
      searchRequests: {
        searches: [
          {
            filter_by: `${formatToTypesenseFilter('Hardcover')} && ${conditionToTypesenseFilter(selectedCondition)} && ${titleIdToTypesenseFilter(title.id)}`,
          },
          {
            filter_by: `${formatToTypesenseFilter('Paperback')} && ${conditionToTypesenseFilter(selectedCondition)} && ${titleIdToTypesenseFilter(title.id)}`,
          },
          {
            filter_by: `${conditionToTypesenseFilter('Used')} && ${titleIdToTypesenseFilter(title.id)}`,
          },
          {
            filter_by: `${conditionToTypesenseFilter('New')} && ${titleIdToTypesenseFilter(title.id)}`
          }
        ] as MultiSearchRequestSchema[]
      }
    }
  });

  const hardcoverFormatResults = searchRequest.data?.results[0];
  const paperbackFormatResults = searchRequest.data?.results[1];
  const usedConditionResults = searchRequest.data?.results[2];
  const newConditionResults = searchRequest.data?.results[3];

  function renderAttributeOption(text, active, onClick, disabled) {
    return (
      <div
        onClick={!disabled ? onClick : null}
        className={classNames(styles['title-attributes-option-pill'], {
          [styles['title-attributes-option-pill--active']]: active,
          [styles['title-attributes-option-pill--disabled']]: disabled || loading,
        })}
      >
        <span className={styles['title-attributes-option-pill-text']}>{text}</span>
      </div>
    );
  }

  if (!title.copies) return null;

  return (
    <div className={styles['title-attributes']}>
      <div className={styles['title-attributes-option-container']}>
        <div className={styles['title-attributes-option']}>
          <h3>Condition</h3>
          {renderAttributeOption(
            'Used',
            selectedCondition === 'Used',
            () => handleFacetClick({ condition: 'Used' }),
            usedConditionResults?.found === 0
          )}
          {renderAttributeOption(
            'New / Like New',
            selectedCondition === 'New',
            () => handleFacetClick({ condition: 'New' }),
            newConditionResults?.found === 0
          )}
        </div>
        <div className={styles['title-attributes-option']}>
          <h3>Format</h3>
          {renderAttributeOption(
            'Paperback',
            selectedFormat === 'Paperback',
            () => handleFacetClick({ format: 'Paperback' }),
            paperbackFormatResults?.found === 0
          )}
          {renderAttributeOption(
            'Hardcover',
            selectedFormat === 'Hardcover',
            () => handleFacetClick({ format: 'Hardcover' }),
            hardcoverFormatResults?.found === 0
          )}
        </div>
      </div>
    </div>
  );
}
